import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BotIcon, ExternalLinkIcon, MessageCircleIcon, SendIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../../contexts/StoreContext';
import { AssistantReply, answer, startChips } from '../../utils/assistant';
import { getQuickQuote } from '../../utils/finance';
import { formatARS } from '../../utils/format';
import { buildWhatsAppUrl } from '../../utils/validation';
interface Message extends AssistantReply {
  id: number;
  from: 'bot' | 'user';
}
const ease = [0.23, 1, 0.32, 1] as const;
const welcome: Message = {
  id: 0,
  from: 'bot',
  text: '¡Hola! Soy el asistente virtual de VCARS. ¿En qué te ayudo?',
  chips: startChips
};
export function VirtualAssistant() {
  const {
    vehicles,
    brands,
    compareIds,
    settings
  } = useStore();
  const {
    pathname
  } = useLocation();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([welcome]);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [messages, typing, reduce]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  if (pathname.startsWith('/admin')) return null;
  const compareBarVisible = compareIds.length > 0 && !pathname.startsWith('/comparar');
  const send = (raw: string) => {
    const text = raw.trim();
    if (!text || typing) return;
    setMessages(m => [...m, {
      id: nextId.current++,
      from: 'user',
      text
    }]);
    setInput('');
    setTyping(true);
    window.setTimeout(() => {
      setMessages(m => [...m, {
        id: nextId.current++,
        from: 'bot',
        ...answer(text, vehicles, brands)
      }]);
      setTyping(false);
    }, 450);
  };
  const openWhatsApp = (text: string) => {
    if (!settings.whatsapp) {
      toast.error('El WhatsApp comercial todavía no está configurado.');
      return;
    }
    window.open(buildWhatsAppUrl(settings.whatsapp, text), '_blank', 'noopener,noreferrer');
  };
  const last = messages[messages.length - 1];
  return <div className={`fixed right-4 z-40 sm:right-6 ${compareBarVisible ? 'bottom-24' : 'bottom-5'} transition-[bottom] duration-200 ease-out`} data-id="element-349" data-mp="39jza43m9kys9">
      <AnimatePresence data-id="element-350" data-mp="1y6ewwatlbszn">
        {open && <motion.section role="dialog" aria-label="Asistente virtual VCARS" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 12,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 12,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease
      }} style={{
        transformOrigin: 'bottom right'
      }} className="absolute bottom-16 right-0 flex h-[min(560px,calc(100vh-8rem))] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-neon/50 bg-surface shadow-[0_20px_60px_-15px_rgba(20,99,255,0.6)]" data-id="element-351" data-mp="1y887g17bqxcx">
            <header className="flex items-center gap-3 border-b border-line bg-panel px-4 py-3" data-id="element-352" data-mp="weccko4ri78c">
              <span className="relative grid h-9 w-9 place-items-center rounded-full bg-neon text-white" data-id="element-353" data-mp="11dxff9n1yzqc">
                <BotIcon className="h-5 w-5" aria-hidden data-id="element-354" data-mp="16c8q1ml69e9m" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-panel bg-emerald-400" aria-hidden data-id="element-355" data-mp="335jzer8j1udb" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-356" data-mp="3856llq6c2ky9">
                <p className="font-display text-sm font-bold tracking-wide" data-id="element-357" data-mp="12g49xa70hmvn">ASISTENTE VCARS</p>
                <p className="text-xs text-mist" data-id="element-358" data-mp="xh9r4891nybj">Respuestas automáticas · 24 h</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-md p-1.5 text-mist hover:bg-white/5 hover:text-white" aria-label="Cerrar asistente" data-id="element-359" data-mp="1bgspi9c1zvan">
                <XIcon className="h-5 w-5" data-id="element-360" data-mp="17dcsvczzhuxy" />
              </button>
            </header>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite" data-id="element-361" data-mp="jq4qp3xur205">
              {messages.map(m => <div key={m.id} className={m.from === 'user' ? 'flex justify-end' : ''} data-id="element-362" data-mp="siq6cb66sskr">
                  <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.from === 'user' ? 'rounded-br-md bg-neon text-white' : 'rounded-bl-md border border-line bg-panel text-silver-hi'}`} data-id="element-363" data-mp="nrq9t70zccis">
                    {m.text}
                  </div>

                  {m.from === 'bot' && m.vehicleIds && m.vehicleIds.length > 0 && <ul className="mt-2 space-y-2" data-id="element-364" data-mp="3mt9j339lj854">
                      {m.vehicleIds.map(id => {
                const v = vehicles.find(x => x.id === id);
                if (!v) return null;
                const brand = brands.find(b => b.id === v.brandId);
                const quote = getQuickQuote(v, brands);
                const cuota = quote?.sim?.firstInstallment;
                return <li key={id} data-id="element-365" data-mp="3qubipl7fdm7m">
                            <Link to={`/vehiculo/${v.id}`} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl border border-line bg-ink/60 p-2 transition-colors duration-150 hover:border-neon/60" data-id="element-366" data-mp="3qtjpui57q2l1">
                              <img src={v.images[0]} alt="" className="h-12 w-16 shrink-0 rounded-lg object-cover" referrerPolicy="no-referrer" data-id="element-367" data-mp="qvacyjaukj2m" />
                              <span className="min-w-0 flex-1" data-id="element-368" data-mp="lxich0w4zomt">
                                <span className="block truncate text-sm font-semibold" data-id="element-369" data-mp="h5791o26tkpi">{brand?.name} {v.model}</span>
                                <span className="block truncate text-xs text-mist" data-id="element-370" data-mp="c75t3c0261i9">{v.version}</span>
                                <span className="block text-xs text-neon-hi" data-id="element-371" data-mp="77jdivwrc96a">
                                  {formatARS(v.price)}
                                  {cuota ? ` · cuota ref. ${formatARS(cuota)}` : ''}
                                </span>
                              </span>
                            </Link>
                          </li>;
              })}
                    </ul>}

                  {m.from === 'bot' && (m.links?.length || m.whatsapp) && <div className="mt-2 flex flex-wrap gap-2" data-id="element-372" data-mp="1nm2k2rfsz0s9">
                      {m.links?.map(l => l.external ? <a key={l.to} href={l.to} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-neon/60 px-3 py-1.5 text-xs font-semibold text-neon-hi hover:bg-neon/10" data-id="element-373" data-mp="203vf0jyzq995">
                            {l.label}
                            <ExternalLinkIcon className="h-3 w-3" aria-hidden data-id="element-374" data-mp="29vstq0p7jjcm" />
                          </a> : <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="rounded-full border border-neon/60 px-3 py-1.5 text-xs font-semibold text-neon-hi hover:bg-neon/10" data-id="element-375" data-mp="1h4hlg356b0j6">
                            {l.label}
                          </Link>)}
                      {m.whatsapp && <button type="button" onClick={() => openWhatsApp(m.whatsapp as string)} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-400" data-id="element-376" data-mp="2xlvio7xts970">
                          <MessageCircleIcon className="h-3.5 w-3.5" aria-hidden data-id="element-377" data-mp="3ioctwyuwzzjn" />
                          Hablar por WhatsApp
                        </button>}
                    </div>}
                </div>)}

              {typing && <div className="flex w-fit gap-1 rounded-2xl rounded-bl-md border border-line bg-panel px-3.5 py-3" aria-label="Escribiendo" data-id="element-378" data-mp="a78jrd0uqwp7">
                  {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-mist" animate={reduce ? undefined : {
              opacity: [0.3, 1, 0.3]
            }} transition={{
              duration: 0.9,
              repeat: Infinity,
              delay: i * 0.15,
              ease: 'linear'
            }} data-id="element-379" data-mp="k5pe8jz01tg0" />)}
                </div>}

              {!typing && last.from === 'bot' && last.chips && <div className="flex flex-wrap gap-2 pt-1" data-id="element-380" data-mp="wgyu6oi2zjam">
                  {last.chips.map(c => <button key={c} type="button" onClick={() => c === 'Hablar con un asesor' ? openWhatsApp('Hola VCARS, quiero hablar con un asesor.') : send(c)} className="rounded-full border border-line bg-ink/60 px-3 py-1.5 text-xs font-medium text-silver-hi transition-colors duration-150 hover:border-neon/60 hover:text-white" data-id="element-381" data-mp="11hcsvfxslgvz">
                      {c}
                    </button>)}
                </div>}
            </div>

            <form onSubmit={e => {
          e.preventDefault();
          send(input);
        }} className="flex items-center gap-2 border-t border-line bg-panel p-3" data-id="element-382" data-mp="8re2nh7v3dpq">
              <label htmlFor="assistant-input" className="sr-only" data-id="element-383" data-mp="2329k7910y34n">Escribí tu consulta</label>
              <input id="assistant-input" ref={inputRef} value={input} onChange={e => setInput(e.target.value)} maxLength={300} placeholder="Ej.: cuota del Cronos" className="h-10 min-w-0 flex-1 rounded-full border border-line bg-ink px-4 text-sm text-white placeholder:text-mist focus:border-neon focus:outline-none" data-id="element-384" data-mp="3o6hin9pp6yqk" />
              <button type="submit" disabled={!input.trim() || typing} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-neon text-white transition-opacity duration-150 disabled:opacity-40" aria-label="Enviar" data-id="element-385" data-mp="ft0um1yr3g0t">
                <SendIcon className="h-4 w-4" data-id="element-386" data-mp="5t08k89hylsx" />
              </button>
            </form>
          </motion.section>}
      </AnimatePresence>

      <motion.button type="button" onClick={() => setOpen(o => !o)} whileTap={{
      scale: 0.95
    }} transition={{
      duration: 0.12
    }} aria-expanded={open} aria-label={open ? 'Cerrar asistente virtual' : 'Abrir asistente virtual'} className="group flex h-14 items-center gap-2 rounded-full border border-neon-hi/70 bg-neon pl-4 pr-5 text-white shadow-[0_0_24px_rgba(20,99,255,0.6)]" data-id="element-387" data-mp="2qkk7xyhk7xp6">
        {open ? <XIcon className="h-6 w-6" aria-hidden data-id="element-388" data-mp="1dmrzjfk7x32x" /> : <BotIcon className="h-6 w-6" aria-hidden data-id="element-389" data-mp="pwfvwgw09si5" />}
        <span className="hidden text-sm font-bold tracking-wide sm:inline" data-id="element-390" data-mp="px7e8q0ldtqh">{open ? 'CERRAR' : '¿TE AYUDO?'}</span>
      </motion.button>
    </div>;
}